"use client";

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/providers/authProvider";
import {
  getEpisodeProgressKey,
  getSeriesWatchProgress,
  indexSeriesWatchProgress,
} from "@/utils/watchTrackerService";

/**
 * Loads the active profile's saved positions for a series and exposes a
 * season+episode lookup. Progress is optional UI enhancement: request failures
 * leave the episode list usable without an error state.
 */
export function useSeriesWatchProgress(contentKey, { enabled = true } = {}) {
  const { isAuthenticated, activeProfile } = useAuth();
  const activeProfileId = activeProfile?._id || activeProfile?.profileId || "";
  const [results, setResults] = useState([]);

  useEffect(() => {
    const controller = new AbortController();
    let isCurrent = true;

    if (!enabled || !isAuthenticated || !contentKey) {
      setResults([]);
      return () => controller.abort();
    }

    setResults([]);
    getSeriesWatchProgress({ contentKey, signal: controller.signal })
      .then((nextResults) => {
        if (isCurrent) setResults(nextResults);
      })
      .catch((error) => {
        if (error?.code !== "ERR_CANCELED" && isCurrent) setResults([]);
      });

    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [activeProfileId, contentKey, enabled, isAuthenticated]);

  const progressByEpisode = useMemo(
    () => indexSeriesWatchProgress(results),
    [results]
  );

  const getEpisodeProgress = (seasonNumber, episodeNumber) =>
    progressByEpisode.get(getEpisodeProgressKey(seasonNumber, episodeNumber)) ||
    null;

  return { results, progressByEpisode, getEpisodeProgress };
}

export default useSeriesWatchProgress;
